import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { authHeaders, resolveUrl } from '../api';
import type { QContext } from './qClient';

/**
 * CV1: ChatGPT-rösten i Q – OpenAI Realtime över WebRTC, det tredje röstalternativet bredvid Apple Lokal och
 * Anthropic + ElevenLabs (som inte rörs). Ljudet går direkt mellan webbvyn och OpenAI; API:t (q/realtime.ts)
 * ger en kortlivad klientnyckel (ek_…) med rätt konto, modell och Q:s verktyg, och kör verktygsanropen som
 * personen. Ingen långlivad nyckel finns här.
 *
 * Varför i webbvyn och inte nativt: samma väg som jaktmötets LiveKit (getUserMedia i WKWebView), så att
 * jaktens mikrofonpaus (huntPause, QB1) gäller oförändrad och Q:s ljudsession i QVoicePlugin inte rörs alls
 * (kraschfixen 0c52a05a). Priset: WKWebView stänger mikrofonen när appen går i bakgrunden – se
 * docs/uppdrag-chatgpt-voice.md, "Begränsningar".
 *
 * CV4b: i iPhone-appen går ljudet i stället genom QRealtimePlugin (Swift, WebSocket + AVAudioEngine, UIBackgroundModes
 * audio), så att samtalet fortsätter med låst skärm. Samtalet, händelserna och verktygsbryggan är kvar här; i
 * bakgrunden kör pluginen verktygen själv (se nativeTransport). Webbläsare, Android och äldre appbyggen: WebRTC.
 *
 * Händelserna följer OpenAI:s GA-format (developers.openai.com/api/docs/guides/voice-webrtc och
 * realtime-conversations, 1 nov 2026).
 */

export type RtState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'reconnecting' | 'error';
export type RtAccount = 'qvenari' | 'own';

export interface RtStatus {
  available: boolean;
  mock: boolean;
  model: string;
  qvenari: { set: boolean; used: number; limit: number | null };
  own: { set: boolean; last4: string | null; updatedAt: string | null };
}

export interface RtSession { clientSecret: string; expiresAt: number; model: string; account: RtAccount; maxMinutes: number; used?: number; limit?: number | null; mock?: boolean }

export interface RtToolResult {
  status: 'done' | 'error' | 'confirm_required' | 'cancelled';
  name: string;
  level: number;
  doing: string;
  question?: string;
  requiresTap?: boolean;
  output: string;
}

export class RtHttpError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(resolveUrl(path), { method, headers: { ...authHeaders(), ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  } catch {
    throw new RtHttpError('Ingen kontakt med Qvenari. Kontrollera nätet.', 0);
  }
  const data = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) throw new RtHttpError(data.error || `Fel ${res.status}`, res.status);
  return data as T;
}

export const rtApi = {
  status: () => call<RtStatus>('GET', '/api/q/realtime/status'),
  saveKey: (key: string) => call<RtStatus>('PUT', '/api/q/realtime/key', { key }),
  removeKey: () => call<RtStatus>('DELETE', '/api/q/realtime/key'),
  session: (account: RtAccount, context?: QContext) => call<RtSession>('POST', '/api/q/realtime/session', { account, context }),
  tool: (b: { name: string; arguments: string; action: 'run' | 'confirm' | 'cancel'; tap?: boolean; context?: QContext }) => call<RtToolResult>('POST', '/api/q/realtime/tool', b),
  /** Ljudvägen och WebRTC-statistiken (felsökning av sprak). Ingen text, inga nycklar. */
  diag: (stage: 'start' | 'end', info: Record<string, unknown>) => call<{ ok: boolean }>('POST', '/api/q/realtime/diag', { stage, info }),
};
export type RtApi = typeof rtApi;

// ---------- Transport ----------

export type RtEvent = { type: string; [k: string]: any };

export interface RtTransport {
  /** Ansluter med klientnyckeln. onClose kommer när förbindelsen bryts (inte efter close()). */
  connect(session: RtSession, on: { event: (e: RtEvent) => void; close: (reason: string) => void }): Promise<void>;
  send(e: RtEvent): void;
  close(): void;
  /** Bara den native transporten: webbens väntande bekräftelse och kontext (qvenari.pending/context), inte till OpenAI. */
  control?(e: RtEvent): void;
}

// iOS: ljudsessionen före och efter samtalet (QVoicePlugin.prepareCall/endCall/audioInfo); på Android rör pluginen
// inte ljudläget (WebView sköter det) utan avbryter bara Q:s egen uppläsning. Saknas i webben och i äldre
// appbyggen – då görs ingenting.
interface CallAudioApi {
  prepareCall(o: { huntCall: boolean }): Promise<Record<string, unknown>>;
  endCall(): Promise<void>;
}
const QVoiceNative = registerPlugin<CallAudioApi>('QVoice');
const nativeAudio = (): CallAudioApi | null => {
  const cap = (window as any).Capacitor;
  return cap?.isNativePlatform?.() && cap.isPluginAvailable?.('QVoice') ? QVoiceNative : null;
};

/** Det som avslöjar sprak i WebRTC: förlorade och utfyllda (concealed) ljudprov, jitter och bufferten. */
export async function audioStats(pc: RTCPeerConnection): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  const report = await pc.getStats();
  report.forEach((r: any) => {
    if (r.type === 'inbound-rtp' && (r.kind === 'audio' || r.mediaType === 'audio')) {
      Object.assign(out, {
        packetsReceived: r.packetsReceived ?? 0, packetsLost: r.packetsLost ?? 0, jitterMs: Math.round((r.jitter ?? 0) * 1000),
        totalSamples: r.totalSamplesReceived ?? 0, concealedSamples: r.concealedSamples ?? 0, concealmentEvents: r.concealmentEvents ?? 0,
        jitterBufferMs: r.jitterBufferEmittedCount ? Math.round((r.jitterBufferDelay / r.jitterBufferEmittedCount) * 1000) : 0,
      });
    }
    if (r.type === 'candidate-pair' && r.nominated) out.rttMs = Math.round((r.currentRoundTripTime ?? 0) * 1000);
  });
  if (out.totalSamples) out.concealedPct = Math.round((out.concealedSamples / out.totalSamples) * 1000) / 10;
  return out;
}

/** WebRTC mot OpenAI: mikrofonen som spår, svaret som ljudspår, händelserna i datakanalen "oai-events". */
export function webrtcTransport(opts: { huntCall?: boolean; diag?: RtApi['diag'] } = {}): RtTransport {
  let pc: RTCPeerConnection | null = null;
  let dc: RTCDataChannel | null = null;
  let mic: MediaStream | null = null;
  let audio: HTMLAudioElement | null = null;
  let closing = false;
  let dropTimer: ReturnType<typeof setTimeout> | null = null;

  let started = 0;
  const cleanup = () => {
    if (pc && opts.diag && started) {
      const seconds = Math.round((Date.now() - started) / 1000);
      void audioStats(pc).then(st => opts.diag?.('end', { ...st, seconds })).catch(() => undefined);
    }
    started = 0;
    if (dropTimer) clearTimeout(dropTimer);
    dc?.close();
    pc?.close();
    mic?.getTracks().forEach(t => t.stop());
    if (audio) { audio.srcObject = null; audio.remove(); }
    pc = null; dc = null; mic = null; audio = null;
  };

  return {
    async connect(session, on) {
      closing = false;
      // iOS: röstläge, bara HFP och 48 kHz/20 ms före mikrofonen (inte under jaktsamtal). Se QVoicePlugin.prepareCall.
      const audioInfo = await nativeAudio()?.prepareCall({ huntCall: !!opts.huntCall }).catch(() => null);
      // Bluetooth och högtalare: systemets ljudväg (WKWebView väljer AirPods/bilens högtalare som för LiveKit).
      // Ingen automatisk förstärkning: den höjer mikrofonen när Q tystnar och drar upp högtalarens eget eko, som
      // ekosläckningen då inte hinner ta bort (sprak). Ekosläckning och brusreducering behålls.
      mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false } });
      pc = new RTCPeerConnection();
      audio = document.createElement('audio');
      audio.autoplay = true;
      audio.setAttribute('playsinline', '');
      audio.style.display = 'none';
      document.body.appendChild(audio);
      pc.ontrack = e => { if (audio) { audio.srcObject = e.streams[0]; void audio.play().catch(() => undefined); } };
      mic.getTracks().forEach(t => pc!.addTrack(t, mic!));
      dc = pc.createDataChannel('oai-events');
      dc.onmessage = m => { try { on.event(JSON.parse(m.data)); } catch { /* inte json */ } };
      dc.onclose = () => { if (!closing) on.close('datachannel'); };
      pc.onconnectionstatechange = () => {
        const s = pc?.connectionState;
        if (s === 'failed') { if (!closing) on.close('failed'); return; }
        if (s === 'disconnected') {
          // Kort avbrott (byte mellan wifi och mobilnät) hämtar sig ofta själv.
          if (dropTimer) clearTimeout(dropTimer);
          dropTimer = setTimeout(() => { if (!closing && pc?.connectionState === 'disconnected') on.close('disconnected'); }, 4000);
        }
      };
      const opened = new Promise<void>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('OpenAI svarade inte i tid.')), 15_000);
        dc!.onopen = () => { clearTimeout(t); resolve(); };
      });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const res = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        body: offer.sdp,
        headers: { Authorization: `Bearer ${session.clientSecret}`, 'Content-Type': 'application/sdp' },
      }).catch(() => { throw new Error('Kunde inte nå OpenAI.'); });
      if (!res.ok) {
        cleanup();
        throw new Error(res.status === 401 ? 'OpenAI godkände inte sessionen. Försök igen.' : `OpenAI svarade med fel ${res.status}.`);
      }
      await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
      await opened;
      started = Date.now();
      const track = mic.getAudioTracks()[0]?.getSettings?.() ?? {};
      void opts.diag?.('start', { native: audioInfo ?? null, huntCall: !!opts.huntCall, micSampleRate: (track as any).sampleRate ?? null, echoCancellation: track.echoCancellation ?? null }).catch(() => undefined);
    },
    send(e) { if (dc?.readyState === 'open') dc.send(JSON.stringify(e)); },
    close() { closing = true; cleanup(); void nativeAudio()?.endCall().catch(() => undefined); },
  };
}

// ---------- iPhone-appen: native transport (CV4b) ----------

interface NativeRealtimeApi {
  isAvailable(): Promise<{ available: boolean }>;
  connect(o: { clientSecret: string; model: string; maxMinutes: number; huntCall: boolean; toolUrl: string; headers: Record<string, string>; context?: QContext }): Promise<Record<string, unknown>>;
  send(o: { event: RtEvent }): Promise<void>;
  control(o: { event: RtEvent }): Promise<void>;
  close(): Promise<Record<string, unknown>>;
  addListener(event: 'event', cb: (e: { json: string }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'close', cb: (e: { reason: string }) => void): Promise<PluginListenerHandle>;
}
const QRealtimeNative = registerPlugin<NativeRealtimeApi>('QRealtime');

/** Finns den native transporten (iPhone-appen från CV4b)? Android, webbläsare och äldre byggen: nej. */
export function nativeRealtimeAvailable(): boolean {
  const cap = (window as any).Capacitor;
  return !!(cap?.isNativePlatform?.() && cap.getPlatform?.() === 'ios' && cap.isPluginAvailable?.('QRealtime'));
}

/**
 * Realtime över QRealtimePlugin: ljudet i appen (fortsätter i bakgrunden), händelserna hit som från datakanalen.
 * Verktygen: när appen är aktiv kör webben dem som förut. I bakgrunden sover webbvyn, så pluginen anropar
 * /api/q/realtime/tool själv med samma inloggning (headers) och nivåregler; de händelserna kommer med
 * qvenari_native: true, och korten/bekräftelsen som qvenari.tool/qvenari.pending när webbvyn vaknar.
 */
export function nativeTransport(opts: { huntCall?: boolean; diag?: RtApi['diag']; getContext?: () => Promise<QContext | undefined>; plugin?: NativeRealtimeApi } = {}): RtTransport {
  const plugin = opts.plugin ?? QRealtimeNative;
  let handles: PluginListenerHandle[] = [];
  let closing = false;
  const unlisten = () => { handles.forEach(h => void h.remove()); handles = []; };

  return {
    async connect(session, on) {
      closing = false;
      unlisten();
      handles = await Promise.all([
        plugin.addListener('event', e => { try { on.event(JSON.parse(e.json)); } catch { /* inte json */ } }),
        plugin.addListener('close', e => { if (!closing) on.close(e.reason || 'closed'); }),
      ]);
      const context = await opts.getContext?.().catch(() => undefined);
      try {
        const info = await plugin.connect({
          clientSecret: session.clientSecret, model: session.model, maxMinutes: session.maxMinutes, huntCall: !!opts.huntCall,
          toolUrl: new URL(resolveUrl('/api/q/realtime/tool'), window.location.href).toString(), headers: authHeaders(), context,
        });
        void opts.diag?.('start', { native: info, huntCall: !!opts.huntCall }).catch(() => undefined);
      } catch (err) {
        unlisten();
        throw new Error((err as Error)?.message || 'Kunde inte nå OpenAI.');
      }
    },
    send(e) { void plugin.send({ event: e }).catch(() => undefined); },
    control(e) { void plugin.control({ event: e }).catch(() => undefined); },
    close() {
      closing = true;
      unlisten();
      void plugin.close().then(st => opts.diag?.('end', { transport: 'native', ...st })).catch(() => undefined);
    },
  };
}

/**
 * Attrappen (API:t med Q_OPENAI_MOCK=1, aldrig i drift): inget ljud och ingen OpenAI. Det personen "säger" skickas
 * med window.__qRealtimeMock.say("…"), och svaren följer ett litet manus – bland annat testfallet "Registrera en
 * skjuten dovhjort, hind, på min position" – så att tillstånd, bekräftelser och verktygsbryggan går att prova.
 */
export function mockTransport(): RtTransport {
  let on: { event: (e: RtEvent) => void; close: (reason: string) => void } | null = null;
  let n = 0;
  let plan: 'kill' | null = null;
  const id = () => `mock_${++n}`;
  const emit = (e: RtEvent, ms = 0) => setTimeout(() => on?.event(e), ms);
  const say = (text: string, ms = 50) => {
    emit({ type: 'response.created' }, ms);
    emit({ type: 'output_audio_buffer.started' }, ms + 30);
    emit({ type: 'response.output_audio_transcript.done', transcript: text }, ms + 60);
    emit({ type: 'output_audio_buffer.stopped' }, ms + 500);
    emit({ type: 'response.done', response: { status: 'completed', output: [] } }, ms + 510);
  };
  const callTool = (name: string, args: Record<string, unknown>) => {
    emit({ type: 'response.created' });
    emit({ type: 'response.done', response: { status: 'completed', output: [{ type: 'function_call', name, call_id: id(), arguments: JSON.stringify(args) }] } }, 40);
  };
  const outputs: { status?: string; result?: string; question?: string; error?: string }[] = [];

  return {
    async connect(_s, handlers) {
      on = handlers;
      (window as any).__qRealtimeMock = {
        say(text: string) {
          emit({ type: 'input_audio_buffer.speech_started' });
          emit({ type: 'input_audio_buffer.speech_stopped' }, 20);
          emit({ type: 'conversation.item.input_audio_transcription.completed', transcript: text }, 30);
          setTimeout(() => {
            if (/dovhjort/i.test(text)) { plan = 'kill'; callTool('list_my_marks', {}); }
            else if (/^\s*ja\b/i.test(text)) callTool('q_confirm', {});
            else if (/^\s*nej\b/i.test(text)) callTool('q_cancel', {});
            else say(`Jag hörde: ${text}`);
          }, 60);
        },
        drop() { on?.close('failed'); },
      };
      emit({ type: 'session.created' });
    },
    send(e) {
      if (e.type === 'conversation.item.create' && e.item?.type === 'function_call_output') {
        try { outputs.push(JSON.parse(e.item.output)); } catch { outputs.push({}); }
        return;
      }
      if (e.type === 'conversation.item.create' && e.item?.type === 'message' && /^\[Qvenari\]/.test(e.item.content?.[0]?.text ?? '')) {
        outputs.push(/"status":"done"/.test(e.item.content[0].text) ? { status: 'done' } : { status: 'error' });
        return;
      }
      if (e.type === 'response.cancel') { emit({ type: 'output_audio_buffer.stopped' }); return; }
      if (e.type !== 'response.create') return;
      const last = outputs[outputs.length - 1];
      if (plan === 'kill' && last?.status === 'done' && last.result?.startsWith('[')) {
        plan = null;
        let marks: { id: string }[] = [];
        try { marks = JSON.parse(last.result); } catch { /* tom */ }
        if (!marks[0]) { say('Du har ingen jaktmark att registrera på.'); return; }
        callTool('register_kill', { markId: marks[0].id, gameType: 'Dovhjort', sex: 'Hona', confirm_text: 'Ska jag registrera en dovhjort, hind, som fälld på din position?' });
        return;
      }
      if (last?.status === 'confirm_required') { say(last.question ?? 'Ska jag göra det?'); return; }
      if (last?.status === 'done') { say('Klart, det är registrerat.'); return; }
      if (last?.status === 'cancelled') { say('Okej, jag låter bli.'); return; }
      say(last?.error ? `Det gick inte: ${last.error}` : 'Det gick inte.');
    },
    close() { on = null; delete (window as any).__qRealtimeMock; },
  };
}

// ---------- Samtalet ----------

export interface RtPending { callId: string; name: string; args: string; question: string; requiresTap: boolean; doing: string }

export interface RtHandlers {
  state(s: RtState): void;
  /** En färdig replik (transkriberad). */
  transcript(role: 'user' | 'assistant', text: string): void;
  /** Verktygskortet; hidden tar bort det (frågan som väntar på bekräftelse visas som eget kort). */
  tool(e: { id: string; text: string; status: 'running' | 'done' | 'error' | 'hidden' }): void;
  pending(p: RtPending | null): void;
  error(message: string): void;
  /** Samtalet är slut (stopp, fel eller tidsgränsen). */
  ended(reason: 'stopped' | 'error' | 'timeout'): void;
}

export interface RtOptions {
  account: RtAccount;
  getContext: () => Promise<QContext | undefined>;
  /** De senaste replikerna, som läggs in igen vid återanslutning (följdfrågor i samma samtal). */
  recent: () => { role: 'user' | 'assistant'; text: string }[];
  on: RtHandlers;
  api?: RtApi;
  transport?: (s: RtSession) => RtTransport;
  /** Ett jaktsamtal (LiveKit) pågår: ljudsessionen rörs inte. */
  huntCall?: boolean;
  /** iPhone-appen: ljudet i appen så att samtalet fortsätter i bakgrunden (CV4b). Av = WebRTC i webbvyn som förut. */
  background?: boolean;
  maxReconnects?: number;
}

const CONFIRM = 'q_confirm';
const CANCEL = 'q_cancel';

export class ChatGPTVoiceSession {
  private t: RtTransport | null = null;
  private state: RtState = 'idle';
  private stopped = false;
  private reconnects = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pendingCall: RtPending | null = null;
  private api: RtApi;
  private queue: Promise<void> = Promise.resolve();
  private speaking = false;
  private cards = 0;

  constructor(private o: RtOptions) { this.api = o.api ?? rtApi; }

  get current(): RtState { return this.state; }
  get pending(): RtPending | null { return this.pendingCall; }

  private set(s: RtState) { if (s !== this.state) { this.state = s; this.o.on.state(s); } }

  async start(): Promise<void> {
    this.stopped = false;
    this.set('connecting');
    try {
      await this.open(false);
    } catch (err) {
      this.fail((err as Error).message || 'Kunde inte starta ChatGPT-rösten.');
    }
  }

  private async open(reseed: boolean) {
    const session = await this.api.session(this.o.account, await this.o.getContext().catch(() => undefined));
    if (this.stopped) return;
    const t = (this.o.transport ?? (s => (s.mock ? mockTransport()
      : this.o.background && nativeRealtimeAvailable() ? nativeTransport({ huntCall: this.o.huntCall, diag: this.api.diag, getContext: this.o.getContext })
        : webrtcTransport({ huntCall: this.o.huntCall, diag: this.api.diag }))))(session);
    this.t = t;
    await t.connect(session, { event: e => this.onEvent(e), close: r => void this.onDrop(r) });
    if (this.stopped) { t.close(); return; }
    // Efter återanslutning: pluginen ska känna till en bekräftelse som fortfarande väntar.
    if (this.pendingCall) t.control?.({ type: 'qvenari.pending', pending: this.pendingCall });
    if (reseed) this.reseed();
    this.set('listening');
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.end('timeout'), session.maxMinutes * 60_000);
  }

  /** Efter återanslutning: de senaste replikerna som text, så att följdfrågor fungerar. */
  private reseed() {
    for (const m of this.o.recent().slice(-12)) {
      this.t?.send({ type: 'conversation.item.create', item: { type: 'message', role: m.role, content: [{ type: m.role === 'user' ? 'input_text' : 'output_text', text: m.text.slice(0, 2000) }] } });
    }
  }

  private async onDrop(reason: string) {
    if (this.stopped) return;
    this.t?.close();
    this.t = null;
    if (this.reconnects >= (this.o.maxReconnects ?? 2)) { this.fail(reason === 'failed' || reason === 'disconnected' ? 'Förbindelsen med ChatGPT bröts.' : 'Samtalet avbröts.'); return; }
    this.reconnects++;
    this.speaking = false;
    this.set('reconnecting');
    try {
      await new Promise(r => setTimeout(r, 800 * this.reconnects));
      if (!this.stopped) await this.open(true);
    } catch (err) {
      void this.onDrop((err as Error).message);
    }
  }

  private fail(msg: string) {
    this.o.on.error(msg);
    this.set('error');
    this.end('error');
  }

  /** Lägger på. */
  stop() { this.end('stopped'); }

  private end(reason: 'stopped' | 'error' | 'timeout') {
    if (this.stopped) return;
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.t?.close();
    this.t = null;
    this.pendingCall = null;
    this.o.on.pending(null);
    if (reason !== 'error') this.set('idle');
    this.o.on.ended(reason);
  }

  /** Avbryter det Q säger just nu (samtalet fortsätter). Att börja prata avbryter också (server-VAD). */
  interrupt() {
    if (!this.t) return;
    this.t.send({ type: 'response.cancel' });
    this.t.send({ type: 'output_audio_buffer.clear' });
    this.speaking = false;
    this.set('listening');
  }

  /** Skrivet meddelande i samma samtal. */
  sendText(text: string) {
    if (!this.t) return;
    this.t.send({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] } });
    this.t.send({ type: 'response.create' });
    this.o.on.transcript('user', text);
  }

  /** Knappen Godkänn (tap) eller Avbryt på bekräftelsekortet. */
  decide(action: 'confirm' | 'cancel') {
    const p = this.pendingCall;
    if (!p) return;
    this.enqueue(async () => {
      const r = await this.bridge(p, action, action === 'confirm');
      this.clearPending();
      this.t?.send({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `[Qvenari] Personen ${action === 'confirm' ? 'tryckte Godkänn' : 'tryckte Avbryt'}. Resultat: ${r.output}` }] } });
      this.t?.send({ type: 'response.create' });
    });
  }

  private clearPending() { this.setPending(null); }

  /** Kortet här och samma väntande bekräftelse i pluginen (som kan få "ja" medan webbvyn sover). */
  private setPending(p: RtPending | null, mirror = true) {
    this.pendingCall = p;
    this.o.on.pending(p);
    if (mirror) this.t?.control?.({ type: 'qvenari.pending', pending: p });
  }

  private enqueue(fn: () => Promise<void>) {
    this.queue = this.queue.then(fn).catch(err => this.o.on.error((err as Error).message || 'Verktyget gick inte att köra.'));
  }

  private async bridge(p: { callId: string; name: string; args: string; doing?: string }, action: 'run' | 'confirm' | 'cancel', tap = false): Promise<RtToolResult> {
    const id = `${p.callId}:${action}:${++this.cards}`;
    const showCard = action !== 'cancel';
    if (showCard) this.o.on.tool({ id, text: p.doing ?? 'Kollar i Qvenari', status: 'running' });
    try {
      const context = await this.o.getContext().catch(() => undefined);
      if (context) this.t?.control?.({ type: 'qvenari.context', context });
      const r = await this.api.tool({ name: p.name, arguments: p.args, action, tap, context });
      if (showCard) this.o.on.tool({ id, text: r.doing, status: r.status === 'confirm_required' ? 'hidden' : r.status === 'done' ? 'done' : 'error' });
      return r;
    } catch (err) {
      const msg = (err as Error).message || 'Verktyget gick inte att köra.';
      // 409 = nivå 3 utan tryck: kortet för bekräftelsen ligger kvar, inget eget felkort.
      if (showCard) this.o.on.tool({ id, text: p.doing ?? p.name, status: err instanceof RtHttpError && err.status === 409 ? 'hidden' : 'error' });
      return { status: 'error', name: p.name, level: 0, doing: p.name, output: JSON.stringify({ status: 'error', error: msg }) };
    }
  }

  private onEvent(e: RtEvent) {
    switch (e.type) {
      case 'input_audio_buffer.speech_started':
        this.speaking = false;
        this.set('listening');
        break;
      case 'input_audio_buffer.speech_stopped':
      case 'response.created':
        if (!this.speaking) this.set('thinking');
        break;
      case 'output_audio_buffer.started':
        this.speaking = true;
        this.set('speaking');
        break;
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        this.speaking = false;
        this.set('listening');
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (e.transcript?.trim()) this.o.on.transcript('user', e.transcript.trim());
        break;
      case 'response.output_audio_transcript.done':
        if (e.transcript?.trim()) this.o.on.transcript('assistant', e.transcript.trim());
        break;
      case 'response.done': {
        const calls = (e.response?.output ?? []).filter((x: any) => x.type === 'function_call');
        // qvenari_native: appen låg i bakgrunden och pluginen körde verktygen själv.
        if (calls.length && !e.qvenari_native) this.enqueue(() => this.runCalls(calls));
        else if (calls.length) this.set('thinking');
        else if (!this.speaking) this.set('listening');
        break;
      }
      case 'error': {
        const msg = String(e.error?.message ?? '');
        // Ofarliga fel (t.ex. response.cancel utan pågående svar) stoppar inte samtalet.
        if (/cancel|no active response/i.test(msg) || e.error?.code === 'response_cancel_not_active') break;
        this.o.on.error(msg ? `ChatGPT: ${msg}` : 'ChatGPT svarade med ett fel.');
        break;
      }
      case 'session.closed':
        void this.onDrop('closed');
        break;
      // Från QRealtimePlugin (CV4b): det som hände medan webbvyn sov.
      case 'qvenari.tool':
        this.o.on.tool({ id: String(e.id), text: String(e.text ?? ''), status: e.status });
        break;
      case 'qvenari.pending':
        this.setPending(e.pending ?? null, false);
        break;
      case 'qvenari.timeout':
        this.end('timeout');
        break;
    }
  }

  private async runCalls(calls: { name: string; call_id: string; arguments: string }[]) {
    for (const c of calls) {
      let output: string;
      if (c.name === CONFIRM || c.name === CANCEL) {
        const p = this.pendingCall;
        if (!p) output = JSON.stringify({ status: 'error', error: 'Ingenting väntar på bekräftelse.' });
        else {
          const r = await this.bridge(p, c.name === CONFIRM ? 'confirm' : 'cancel');
          // Nivå 3 nekas av servern utan tryck: kortet ligger kvar.
          if (r.status !== 'error' || c.name === CANCEL) this.clearPending();
          output = r.output;
        }
      } else {
        const r = await this.bridge({ callId: c.call_id, name: c.name, args: c.arguments ?? '{}' }, 'run');
        if (r.status === 'confirm_required') {
          this.setPending({ callId: c.call_id, name: c.name, args: c.arguments ?? '{}', question: r.question ?? '', requiresTap: !!r.requiresTap, doing: r.doing });
        }
        output = r.output;
      }
      this.t?.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: c.call_id, output } });
    }
    this.t?.send({ type: 'response.create' });
  }
}
